// Decides what the Organization dropdown should write back when it closes.
//
// The dropdown edits a draft selection while it is open and only commits on close, so every
// close has to answer one question: did anything actually change? Committing an unchanged
// selection is not harmless - Organization.onOrganizationChange rewrites the cached organization
// and clears the remembered NCPDP selection - so merely opening and closing the dropdown must
// reach context as nothing at all.

function ToComparableValue(value) {
  if (value === null || value === undefined) return null;

  const comparable = String(value).trim();

  return comparable === "" ? null : comparable;
}

function ToRpidKeySet(rpids) {
  if (!Array.isArray(rpids)) return [];

  const keys = [];

  rpids.forEach((rpid) => {
    const key = ToComparableValue(rpid);

    if (key !== null && keys.indexOf(key) === -1) keys.push(key);
  });

  return keys;
}

// Compares both value and type. A parent company and a Group are different organizations even
// when they carry the same identifier, and selecting one in place of the other is a real change.
export function AreSameOrganizations(first, second) {
  if (!first || !second) return !first && !second;

  const firstValue = ToComparableValue(first.value);
  const secondValue = ToComparableValue(second.value);

  if (firstValue === null || secondValue === null) return false;

  return (
    firstValue === secondValue &&
    ToComparableValue(first.type) === ToComparableValue(second.type)
  );
}

// Order-insensitive, because the RPIDs describe which parent companies are in play rather than a
// sequence, and the order they were clicked in is not meaningful. Normalized to strings because
// selectedRpidsForGroup holds stringified ItemIds while option values can arrive as numbers.
export function AreSameRpidSets(first, second) {
  const firstKeys = ToRpidKeySet(first);
  const secondKeys = ToRpidKeySet(second);

  if (firstKeys.length !== secondKeys.length) return false;

  return firstKeys.every((key) => secondKeys.indexOf(key) > -1);
}

// Returns the single action that closing the dropdown should take.
//
// "organization" and "rpids" are deliberately separate. Routing an RPID-only change through
// onChange would rewrite the cached organization and clear the remembered NCPDP selection for a
// refinement that never left the Group it refines.
export function GetDraftCommit(draft, current) {
  if (!draft || !draft.organization) return { kind: "none" };

  const draftRpids = ToRpidKeySet(draft.rpids);
  const currentOrganization = current ? current.organization : null;

  if (!AreSameOrganizations(draft.organization, currentOrganization)) {
    return {
      kind: "organization",
      organization: draft.organization,
      rpids: draftRpids,
    };
  }

  const currentRpids = current ? current.rpids : [];

  if (!AreSameRpidSets(draftRpids, currentRpids)) {
    return { kind: "rpids", rpids: draftRpids };
  }

  return { kind: "none" };
}
